import { Graphics } from 'pixi.js';
import type { Application } from 'pixi.js';
import type { World, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	CameraResource,
	EnemyComponent,
	PlatformComponent,
	ProjectileComponent,
	StageBounds,
	TransformComponent
} from '@game/scenes/characterPrototype/types';
import { randomInRange } from '@game/scenes/characterPrototype/math';

export type SpawnPlatformFn = (
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	width: number,
	height: number
) => number;

export type SpawnEnemyFn = (
	world: World,
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	stage: Application['stage'],
	x: number,
	y: number
) => void;

export function handlePlatformGeneration(
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	camera: CameraResource,
	bounds: StageBounds,
	tuning: Tuning,
	spawnPlatform: SpawnPlatformFn
) {
	let nextY = world.getResource<number>('platform-next-y');
	if (nextY === undefined || Number.isNaN(nextY)) {
		nextY = camera.y - tuning.platformGapMin;
	}

	while (nextY > camera.y - tuning.cameraSpawnAhead) {
		const width = randomInRange(tuning.platformMinWidth, tuning.platformMaxWidth);
		const maxX = Math.max(tuning.platformMinSideOffset, bounds.width - tuning.platformMinSideOffset - width);
		const x = randomInRange(tuning.platformMinSideOffset, maxX);
		spawnPlatform(world, platforms, graphics, stage, x, nextY, width, tuning.platformHeight);
		nextY -= randomInRange(tuning.platformGapMin, tuning.platformGapMax);
	}
	world.setResource<number>('platform-next-y', nextY);
}

export function handleEnemyGeneration(
	world: World,
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	stage: Application['stage'],
	camera: CameraResource,
	bounds: StageBounds,
	spawnEnemy: SpawnEnemyFn
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	let nextY = world.getResource<number>('enemy-next-y');
	if (nextY === undefined || Number.isNaN(nextY)) {
		nextY = camera.y - tuning.enemyGapMin;
	}
	while (nextY > camera.y - tuning.enemySpawnAhead) {
		const x = Math.random() < 0.5 ? tuning.enemyMarginX : bounds.width - tuning.enemyMarginX;
		spawnEnemy(world, enemies, transforms, graphics, ammoGraphics, ammos, stage, x, nextY);
		nextY -= randomInRange(tuning.enemyGapMin, tuning.enemyGapMax);
	}
	world.setResource<number>('enemy-next-y', nextY);
}

export function handlePlatformCleanup(
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	camera: CameraResource,
	bounds: StageBounds,
	tuning: Tuning
) {
	const cutoff = camera.y + bounds.height + tuning.platformCleanupBelow;
	const toRemove: number[] = [];
	platforms.forEach((platform, entity) => {
		if (platform.y > cutoff) {
			toRemove.push(entity);
		}
	});
	for (const entity of toRemove) {
		const graphic = graphics.get(entity);
		if (graphic) {
			graphic.parent?.removeChild(graphic);
			graphic.destroy();
		}
		platforms.delete(entity);
		graphics.delete(entity);
		world.destroyEntity(entity);
	}
}

export function handleEnemyCleanup(
	world: World,
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	ammoGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	camera: CameraResource,
	bounds: StageBounds
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	const toRemove: number[] = [];
	const cutoffBottom = camera.y + bounds.height + tuning.platformCleanupBelow;
	const cutoffTop = camera.y - tuning.cameraSpawnAhead * 2;
	enemies.forEach((_enemy, entity) => {
		const transform = transforms.get(entity);
		if (!transform) {
			toRemove.push(entity);
			return;
		}
		if (transform.y > cutoffBottom || transform.y < cutoffTop) {
			toRemove.push(entity);
		}
	});
	for (const entity of toRemove) {
		const g = graphics.get(entity);
		if (g) {
			g.parent?.removeChild(g);
			g.destroy();
		}
		const ag = ammoGraphics.get(entity);
		if (ag) {
			ag.parent?.removeChild(ag);
			ag.destroy();
		}
		ammos.delete(entity);
		ammoGraphics.delete(entity);
		world.destroyEntity(entity);
	}
}

export function handleProjectileCleanup(
	world: World,
	projectiles: ComponentStore<ProjectileComponent>,
	transforms: ComponentStore<TransformComponent>,
	graphics: ComponentStore<Graphics>,
	camera: CameraResource,
	bounds: StageBounds
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	const cutoffBottom = camera.y + bounds.height + tuning.platformCleanupBelow;
	const cutoffTop = camera.y - tuning.cameraSpawnAhead * 2;
	const cutoffSide = Math.max(bounds.width, 800) + 120;
	const toRemove: number[] = [];
	projectiles.forEach((_projectile, entity) => {
		const transform = transforms.get(entity);
		if (!transform) {
			toRemove.push(entity);
			return;
		}
		if (
			transform.y > cutoffBottom ||
			transform.y < cutoffTop ||
			transform.x < -cutoffSide ||
			transform.x > cutoffSide
		) {
			toRemove.push(entity);
		}
	});
	for (const entity of toRemove) {
		const g = graphics.get(entity);
		if (g) {
			g.parent?.removeChild(g);
			g.destroy();
		}
		world.destroyEntity(entity);
	}
}


